import { useState, useContext } from "react";
import { Flag, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, AuthContext } from "../App";

// Modal to report a user. Sends reason + room + a snapshot of recent chat.
const ReportUserModal = ({ target, room = "", recentMessages = [], source = "chat_room", onClose }) => {
  const { token } = useContext(AuthContext);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!reason.trim()) {
      toast.error("Please describe what happened");
      return;
    }
    setSubmitting(true);
    try {
      const chat_log = (recentMessages || [])
        .filter((m) => m && m.content)
        .slice(-30)
        .map((m) => ({ username: m.username, content: m.content, timestamp: m.timestamp }));
      const res = await axios.post(
        `${API}/users/report`,
        {
          reported_user_id: target.user_id,
          reason: reason.trim(),
          room,
          chat_log,
          source,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || "Report submitted. Staff will review it.");
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit report");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4" data-testid="report-user-modal">
      <div className="w-full max-w-md">
      <div className="retro-window">
        <div className="retro-title-bar">
          <span className="text-xs flex items-center gap-1">
            <Flag className="w-3 h-3" /> Report {target?.username}
          </span>
          <button className="retro-button px-1 py-0 text-[10px]" onClick={onClose} data-testid="report-close-btn">×</button>
        </div>
        <div className="bg-[#C0C0C0] p-3 space-y-2">
          <p className="font-arial text-xs text-[#333]">
            Tell our staff what {target?.username} did. Your recent chat in this room
            ({(recentMessages || []).length} messages) will be attached for review.
          </p>
          <textarea
            className="retro-input w-full h-28 text-sm p-2"
            placeholder="Describe the issue (harassment, spam, inappropriate content, etc.)"
            value={reason}
            maxLength={1000}
            onChange={(e) => setReason(e.target.value)}
            data-testid="report-reason-input"
          />
          <div className="flex justify-end gap-2">
            <button className="retro-button px-3 py-1 text-xs flex items-center gap-1" onClick={onClose} data-testid="report-cancel-btn">
              <X className="w-3 h-3" /> Cancel
            </button>
            <button
              className="retro-button px-3 py-1 text-xs bg-red-100 flex items-center gap-1"
              onClick={submit}
              disabled={submitting}
              data-testid="report-submit-btn"
            >
              <Flag className="w-3 h-3 text-red-700" /> {submitting ? "Sending..." : "Submit Report"}
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ReportUserModal;
